// src/pages/DriverPortalPage.tsx
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Phone, MapPin, RefreshCw, CheckCircle2, Clock, Truck, Clipboard, LogOut } from 'lucide-react';

type Driver = {
  id: string;
  name: string;
  age: number | null;
  phone: string | null;
  vehicle_type: 'bike' | 'moped' | 'car';
  status: 'pending' | 'approved' | 'active' | 'inactive';
};

type Order = {
  id: string;
  customer_name: string;
  customer_phone: string | null;
  customer_address: string;
  zone: string | null;
  delivery_fee: number;
  subtotal: number;
  total: number;
  status: 'pending' | 'assigned' | 'delivering' | 'completed' | 'cancelled';
  driver_id: string | null;
  restaurant_id: string | null;
  created_at: string;
};

type OrderItemRow = {
  id: string;
  order_id: string;
  quantity: number;
  price: number;
  size?: string | null;
  menu_item_id: string;
  menu_items?: { name: string } | null; // via FK til menu_items
};

const fmt = (n: number) => `${n.toFixed(2)} kr`;

export default function DriverPortalPage() {
  const [me, setMe] = useState<Driver | null>(null);
  const [lookup, setLookup] = useState('');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [itemsByOrder, setItemsByOrder] = useState<Record<string, OrderItemRow[]>>({});
  const [poll, setPoll] = useState(true);

  // Enkel polling (15s) for oppdaterte ordrer når innlogget
  useEffect(() => {
    if (!me || !poll) return;
    const id = setInterval(() => { void refreshOrders(); }, 15000);
    return () => clearInterval(id);
  }, [me, poll]);

  async function signInDriver() {
    if (!lookup.trim()) {
      alert('Skriv inn telefonnummer eller navn.');
      return;
    }
    setLoading(true);

    // Prøv match på telefon (rensk til siffer) ellers navn (ILIKE)
    const raw = lookup.trim();
    const onlyDigits = raw.replace(/[^\d]/g, '');
    let found: Driver | null = null;

    if (onlyDigits.length >= 5) {
      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .or('status.eq.approved,status.eq.active')
        .ilike('phone', `%${onlyDigits}%`)
        .limit(1)
        .maybeSingle();
      if (!error && data) found = data as Driver;
    }

    if (!found) {
      const { data } = await supabase
        .from('drivers')
        .select('*')
        .or('status.eq.approved,status.eq.active')
        .ilike('name', raw)
        .limit(1)
        .maybeSingle();
      if (data) found = data as Driver;
    }

    setLoading(false);

    if (!found) {
      alert('Fant ingen aktiv sjåfør på det du skrev.');
      return;
    }
    setMe(found);
    // last initialt
    await refreshOrders(found.id);
  }

  async function refreshOrders(driverId?: string) {
    const did = driverId ?? me?.id;
    if (!did) return;
    setLoading(true);

    // Hent de siste 200 ordrene for denne sjåføren
    const { data: dOrders } = await supabase
      .from('orders')
      .select('id, customer_name, customer_phone, customer_address, zone, delivery_fee, subtotal, total, status, driver_id, restaurant_id, created_at')
      .eq('driver_id', did)
      .order('created_at', { ascending: false })
      .limit(200);

    setOrders(dOrders || []);

    // Hent alle ordrelinjer for de ordrene
    const ids = (dOrders || []).map(o => o.id);
    if (ids.length) {
      const { data: dItems } = await supabase
        .from('order_items')
        .select('id, order_id, quantity, price, size, menu_item_id, menu_items(name)')
        .in('order_id', ids);

      const grouped: Record<string, OrderItemRow[]> = {};
      (dItems || []).forEach(oi => {
        (grouped[oi.order_id] ||= []).push(oi);
      });
      setItemsByOrder(grouped);
    } else {
      setItemsByOrder({});
    }

    setLoading(false);
  }

  async function setStatus(orderId: string, status: Order['status']) {
    const { error } = await supabase.from('orders').update({ status }).eq('id', orderId);
    if (error) {
      alert('Kunne ikke oppdatere status.');
      return;
    }
    await refreshOrders();
  }

  function copyAddress(addr: string) {
    navigator.clipboard.writeText(addr).then(
      () => alert('Adresse kopiert'),
      () => alert('Klarte ikke kopiere adresse')
    );
  }

  // Del ordrer i "Aktive" og "Historikk"
  const activeOrders = useMemo(
    () => orders.filter(o => o.status === 'pending' || o.status === 'assigned' || o.status === 'delivering'),
    [orders]
  );
  const historyOrders = useMemo(
    () => orders.filter(o => o.status === 'completed' || o.status === 'cancelled'),
    [orders]
  );

  if (!me) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Sjåførportal</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Identifiser deg med telefonnummer eller fullt navn slik at vi finner oppdragene dine.
          </p>
          <input
            value={lookup}
            onChange={e => setLookup(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white mb-3"
            placeholder="F.eks. +47 999 88 777 eller 'Adrian Vassbø'"
          />
          <button
            onClick={signInDriver}
            disabled={loading}
            className="w-full py-3 rounded-lg bg-[#004D99] hover:bg-[#003366] text-white font-semibold"
          >
            {loading ? 'Sjekker...' : 'Gå videre'}
          </button>
          <p className="text-xs text-gray-500 mt-3">
            Bare aktive/approved sjåfører kan logge inn her.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-5xl mx-auto px-4 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Hei, {me.name.split(' ')[0]} 👋</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {me.vehicle_type === 'bike' ? 'Sykkel' : me.vehicle_type === 'moped' ? 'Moped' : 'Bil'} • {me.status}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void refreshOrders()}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-900 text-white dark:bg-white dark:text-gray-900"
            >
              <RefreshCw className="w-4 h-4" />
              Oppdater
            </button>
            <button
              onClick={() => setPoll(p => !p)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              title="Slå av/på auto-oppdatering"
            >
              {poll ? 'Auto: På' : 'Auto: Av'}
            </button>
            <button
              onClick={() => setMe(null)}
              className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
              title="Logg ut"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Aktive oppdrag */}
        <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Aktive oppdrag</h2>
            <span className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {activeOrders.length} stk
            </span>
          </div>

          {loading && <div className="p-3 text-gray-500">Laster…</div>}

          {!loading && activeOrders.length === 0 && (
            <div className="text-gray-600 dark:text-gray-300">Ingen aktive oppdrag akkurat nå.</div>
          )}

          <div className="space-y-4">
            {activeOrders.map(o => {
              const lines = itemsByOrder[o.id] || [];
              const coins = o.total * 0.01; // 1 % VærCoins til kunde, bare info
              return (
                <div key={o.id} className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-white">
                        {o.customer_name}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        <span>{o.customer_address}</span>
                      </div>
                      {o.customer_phone && (
                        <div className="text-sm text-gray-600 dark:text-gray-300 flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          <a className="underline" href={`tel:${o.customer_phone}`}>{o.customer_phone}</a>
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        Bestilt: {new Date(o.created_at).toLocaleString()} • Sone: {o.zone || '—'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-900 dark:text-white font-semibold">{fmt(o.total)}</div>
                      <div className="text-xs text-gray-500">Kunde får: {coins.toFixed(2)} VærCoins</div>
                      <div className="mt-1 text-xs text-gray-500">Levering: {fmt(o.delivery_fee)}</div>
                    </div>
                  </div>

                  {/* Varer */}
                  <div className="mt-3 rounded bg-gray-50 dark:bg-gray-900 p-3 text-sm">
                    {lines.length ? (
                      <ul className="space-y-1">
                        {lines.map((it, idx) => (
                          <li key={it.id} className="flex justify-between">
                            <span>
                              {idx + 1}. {it.quantity > 1 ? `${it.quantity}x ` : ''}
                              {it.menu_items?.name || 'Ukjent vare'}
                              {it.size ? ` (${String(it.size).toLowerCase()})` : ''}
                            </span>
                            <span className="text-gray-700 dark:text-gray-300">{fmt(it.price * it.quantity)}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-gray-500">Ingen varelinjer registrert.</div>
                    )}
                  </div>

                  {/* Hurtigknapper */}
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {o.status !== 'delivering' && o.status !== 'completed' && (
                      <button
                        onClick={() => void setStatus(o.id, 'delivering')}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#004D99] text-white"
                      >
                        <Truck className="w-4 h-4" />
                        Marker som på vei
                      </button>
                    )}
                    {o.status !== 'completed' && (
                      <button
                        onClick={() => void setStatus(o.id, 'completed')}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Marker som levert
                      </button>
                    )}
                    <button
                      onClick={() => copyAddress(o.customer_address)}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-100"
                    >
                      <Clipboard className="w-4 h-4" />
                      Kopier adresse
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Historikk */}
        <section className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Historikk</h2>
            <span className="text-sm px-2 py-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
              {historyOrders.length} stk
            </span>
          </div>

          {historyOrders.length === 0 ? (
            <div className="text-gray-600 dark:text-gray-300">Ingen historikk ennå.</div>
          ) : (
            <div className="space-y-3">
              {historyOrders.map(o => (
                <div key={o.id} className="flex items-center justify-between rounded border border-gray-200 dark:border-gray-700 p-3">
                  <div className="text-sm">
                    <div className="font-medium text-gray-900 dark:text-white">{o.customer_name}</div>
                    <div className="text-gray-600 dark:text-gray-300">{o.customer_address}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {new Date(o.created_at).toLocaleString()} • {o.status}
                    </div>
                  </div>
                  <div className="text-right text-sm">
                    <div className="text-gray-900 dark:text-white font-semibold">{fmt(o.total)}</div>
                    <div className="text-xs text-gray-500">Levering: {fmt(o.delivery_fee)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
